import React, { FocusEventHandler, useEffect, useState } from 'react';
import { TextField, Autocomplete, Chip } from '@mui/material';
import { FormikTouched } from 'formik/dist/types';

type Props = {
    label?: string;
    name: string;
    data?: any[];
    size?: 'small' | 'medium' | undefined;
    margin?: 'dense' | 'normal' | 'none';
    required?: false | boolean;
    value?: any;
    touched?: boolean | FormikTouched<any> | FormikTouched<any>[] | undefined;
    errors?: any;
    disabled?: boolean;
    onChangeValue?: (event: any) => void;
    inputValueEnabled?: false | boolean;
    onInputValue?: any;
    reasonEnabled?: false | boolean;
    onReason?: any;
    getOptionLabel?: (option: any) => string;
    getOptionValue?: false | boolean;
    defineValue?: string | undefined;
    onBlur?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
    multiple?: boolean;
    placeholder?: string;
};

const AutocompleteField = ({
    label,
    name,
    data,
    size,
    margin,
    required,
    touched,
    errors,
    value,
    disabled,
    onChangeValue,
    onInputValue,
    reasonEnabled,
    onReason,
    inputValueEnabled,
    getOptionLabel,
    getOptionValue,
    defineValue,
    onBlur,
    multiple,
    placeholder,
}: Props) => {
    //onChangeValue Formik
    //The variable autocompleteFormik must be equal to the variable name of the formik
    //onChangeValue={(value) => formik.setFieldValue('autocompleteFormik', value)}

    const [inputValue, setInputValue] = useState<any | null>('');
    const [valueItem, setValueItem] = useState<any | null>('');
    const [reasonItem, setReasonItem] = useState<any | null>('');
    const selectedValues = multiple
        ? (data || []).filter((item) => (value || []).includes(item.id))
        : valueItem || null;

    useEffect(() => {
        if (value != null && value !== '' && reasonItem !== 'clear') {
            const valueProp =
                data?.filter((item) => String(item.id) === String(value)) || [];
            if (valueProp.length > 0) {
                setValueItem(valueProp[0]);
                setInputValue(
                    getOptionValue
                        ? Object.values(valueProp[0])[1] +
                              ' - ' +
                              Object.values(valueProp[0])[2]
                        : Object.values(valueProp[0])[1]
                );
            }
        } else if (!inputValueEnabled) {
            setValueItem('');
            setInputValue('');
        }
    }, [value, data, multiple]);

    return (
        <Autocomplete
            multiple={multiple}
            id={valueItem ? valueItem?.id?.toString() || '' : ''}
            options={data || []}
            disabled={disabled}
            getOptionLabel={
                !getOptionLabel ? (option) => option.name || '' : getOptionLabel
            }
            value={selectedValues}
            onChange={(_: any, newValue: any) => {
                if (!onChangeValue) return;

                if (multiple) {
                    const ids = newValue?.map((item: any) => item.id) || [];
                    onChangeValue(ids);
                } else {
                    setValueItem(newValue || null);

                    onChangeValue(
                        newValue
                            ? defineValue === 'object'
                                ? newValue
                                : defineValue === 'value'
                                    ? newValue.name
                                    : newValue.id
                            : ''
                    );
                }
            }}
            inputValue={inputValue || ''}
            onInputChange={(_: any, newInputValue: string | null, reason) => {
                setInputValue(newInputValue || '');
                setReasonItem(reason);
                if (reasonEnabled) {
                    onReason(reason);
                }
                if (inputValueEnabled) {
                    onInputValue(newInputValue);
                }
                if (reason === 'clear') {
                    setInputValue('');
                    setValueItem(null);
                    if (onChangeValue) {
                        onChangeValue(multiple ? [] : '');
                    }
                }
            }}
            filterSelectedOptions
            isOptionEqualToValue={(option, value) => {
                if (!value || !option) return false;
                return option.id === value.id;
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    name={name}
                    size={size}
                    margin={margin}
                    required={required}
                    placeholder={placeholder}
                    onBlur={onBlur}
                    helperText={touched ? errors : ''}
                    error={touched && Boolean(errors)}
                    sx={{
                        '& label.Mui-focused': {
                            color: 'primary.main',
                        },
                        '& .MuiInput-underline:after': {
                            borderBottomColor: 'primary.main',
                        },
                        '& .MuiFilledInput-underline:after': {
                            borderBottomColor: 'primary.main',
                        },
                        '& .MuiOutlinedInput-root': {
                            '&.Mui-focused fieldset': {
                                borderColor: 'primary.main',
                            },
                        },
                    }}
                />
            )}
            renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => (
                    <Chip
                        label={option.name}
                        {...getTagProps({ index })}
                        key={option.id}
                    />
                ))
            }
        />
    );
};

export default AutocompleteField;
